import { useEffect, useMemo, useState } from "react";
import { Bell, X } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useImpersonation } from "@/contexts/ImpersonationContext";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useIsMobile } from "@/hooks/use-mobile";

type NotificationRow = {
  id: string;
  title: string;
  body: string | null;
  severity: string;
  created_at: string;
  read_at: string | null;
  dismissed_at: string | null;
};

const LAST_SEEN_KEY = "ss_notifications_last_seen";

export function NotificationCenter() {
  const { user } = useAuth();
  const { isImpersonating, activeCustomerId, notifications: impersonationNotifications } = useImpersonation();
  const [open, setOpen] = useState(false);
  const isMobile = useIsMobile();

  const { data: notifications = [], refetch } = useQuery({
    queryKey: ["notifications", user?.id],
    enabled: Boolean(user?.id) && !isImpersonating,
    queryFn: async () => {
      if (!user?.id) return [];
      const { data, error } = await supabase
        .from("notifications")
        .select("id, title, body, severity, created_at, read_at, dismissed_at")
        .eq("user_id", user.id)
        .is("dismissed_at", null)
        .order("created_at", { ascending: false })
        .limit(20);
      if (error) throw error;
      return (data ?? []) as NotificationRow[];
    },
  });

  const effectiveNotifications = isImpersonating ? impersonationNotifications : notifications;
  const unreadIds = useMemo(
    () => effectiveNotifications.filter((item) => !item.read_at).map((item) => item.id),
    [effectiveNotifications],
  );

  const unreadCount = unreadIds.length;

  useEffect(() => {
    if (!effectiveNotifications.length) return;
    const newest = effectiveNotifications[0];
    if (!newest || newest.read_at) return;
    if (isImpersonating && !activeCustomerId) return;
    const key = `${LAST_SEEN_KEY}:${isImpersonating ? activeCustomerId : user?.id}`;
    const lastSeen = localStorage.getItem(key);
    if (lastSeen === newest.id) return;
    toast(newest.title, {
      description: newest.body || "We have an update from ShadowScan.",
    });
    localStorage.setItem(key, newest.id);
  }, [activeCustomerId, effectiveNotifications, isImpersonating, user?.id]);

  useEffect(() => {
    if (!open || unreadIds.length === 0 || !user?.id || isImpersonating) return;
    const markRead = async () => {
      const { error } = await supabase
        .from("notifications")
        .update({ read_at: new Date().toISOString() })
        .in("id", unreadIds);
      if (error) {
        toast.error("Unable to update notifications.");
        return;
      }
      await refetch();
    };
    markRead();
  }, [open, unreadIds, refetch, isImpersonating, user?.id]);

  const handleDismissOne = async (id: string) => {
    if (isImpersonating) {
      toast.message("Notifications are read-only while viewing a customer.");
      return;
    }
    const { error } = await supabase
      .from("notifications")
      .update({ dismissed_at: new Date().toISOString() })
      .eq("id", id);
    if (error) {
      toast.error("Unable to dismiss notification.");
      return;
    }
    await refetch();
  };

  const formatTime = (value: string) =>
    new Date(value).toLocaleString("en-AU", { timeZone: "Australia/Sydney" });

  return (
    <Popover open={open} onOpenChange={setOpen} modal={false}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="relative">
          <Bell className="h-4 w-4" />
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 inline-flex h-4 min-w-[16px] items-center justify-center rounded-full bg-primary px-1 text-[10px] font-semibold text-primary-foreground">
              {unreadCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align={isMobile ? "center" : "end"}
        className="w-[min(380px,calc(100vw-1.5rem))] p-0 overflow-hidden"
      >
        <div className="flex items-center justify-between border-b border-border/60 px-4 py-3">
          <div className="text-sm font-semibold">Notifications</div>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={() => setOpen(false)}
            aria-label="Close notifications"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
        <div className="max-h-[360px] overflow-y-auto">
          {effectiveNotifications.length === 0 && (
            <div className="px-4 py-6 text-sm text-muted-foreground">
              {isImpersonating ? "No customer notifications available." : "No notifications yet."}
            </div>
          )}
              {effectiveNotifications.map((item) => (
            <div
              key={item.id}
              className="border-b border-border/60 px-4 py-3 last:border-b-0"
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-sm font-semibold text-foreground">
                    {item.title}
                  </div>
                  {item.body && (
                    <p className="text-xs text-muted-foreground mt-1">
                      {item.body}
                    </p>
                  )}
                  <div className="mt-2 text-[11px] text-muted-foreground">
                    {formatTime(item.created_at)}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span
                    className={`mt-1 h-2 w-2 rounded-full ${
                      item.severity === "success"
                        ? "bg-emerald-400"
                        : item.severity === "warning"
                          ? "bg-amber-400"
                          : item.severity === "error"
                            ? "bg-rose-400"
                            : "bg-slate-400"
                    }`}
                  />
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6"
                    onClick={() => handleDismissOne(item.id)}
                    aria-label="Dismiss notification"
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
